import { FC } from "react";
import {
  NetworkPortfolioManagementProjectStatus,
  networkPortfolioManagementProjectStatusDisplayLabel,
} from "@app/network-portfolio-management/interfaces";
import { Badge, BadgeClassNames } from "@app/shared/Badge";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome, QesIcons } from "@app/shared/icons/qesIcons";

interface ProjectStatusBadgeProps {
  status: NetworkPortfolioManagementProjectStatus;
}

interface StatusBadgeConfig {
  badgeType: BadgeClassNames;
  iconName?: string;
}

// Badge color + icon per status. "Running" displays as "Processing" with the animated spinner
// (label comes from networkPortfolioManagementProjectStatusDisplayLabel). Publish Pending keeps its
// original icon-less treatment.
const statusBadgeConfig: Record<NetworkPortfolioManagementProjectStatus, StatusBadgeConfig> = {
  [NetworkPortfolioManagementProjectStatus.Canceled]: { badgeType: "badge--neutral", iconName: QesIcons.SlashOctagon },
  [NetworkPortfolioManagementProjectStatus.Complete]: { badgeType: "badge--success", iconName: QesIcons.CheckCircle },
  [NetworkPortfolioManagementProjectStatus.Failed]: { badgeType: "badge--danger", iconName: QesIcons.XCircle },
  [NetworkPortfolioManagementProjectStatus.NeedsAttention]: { badgeType: "badge--warning-2", iconName: QesIcons.AlertTriangle },
  [NetworkPortfolioManagementProjectStatus.NotRun]: { badgeType: "badge--neutral", iconName: QesIcons.MinusCircle },
  [NetworkPortfolioManagementProjectStatus.PublishPending]: { badgeType: "badge--warning" },
  [NetworkPortfolioManagementProjectStatus.Running]: { badgeType: "badge--info", iconName: FontAwesome.Spinner },
};

export const ProjectStatusBadge: FC<ProjectStatusBadgeProps> = ({ status }) => {
  const config = statusBadgeConfig[status];
  if (!config) {
    return null;
  }

  const isRunning = status === NetworkPortfolioManagementProjectStatus.Running;

  return (
    <Badge badgeType={config.badgeType}>
      {config.iconName && (
        <QesIcon
          iconName={config.iconName}
          isDecorative
          className={`network-portfolio-management__status-icon${isRunning ? " network-portfolio-management__status-icon--spinning" : ""}`}
        />
      )}
      {networkPortfolioManagementProjectStatusDisplayLabel[status]}
    </Badge>
  );
};
